import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FaSort, FaSortUp, FaSortDown, FaSearch, FaTimes, FaPlus, FaArrowRight } from 'react-icons/fa';
import { QUIZMASTER_API } from '../config';
import { formatToLocalDateTime, getUtcTimestamp } from '../utils/date';
import '../styles/styles.css';

const DocumentList = ({
    userId,
    setCurrentPage,
    setSelectedDocument,
    newDocument,
    accessToken,
    onSignIn,
    supabaseRefreshToken,
}) => {
    const [allDocuments, setAllDocuments] = useState([]);
    const [isFetching, setIsFetching] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [fetchedPages, setFetchedPages] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [hasMoreDocuments, setHasMoreDocuments] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const documentsPerPage = 3000;

    const fetchDocuments = useCallback(async (pageNumber) => {
        if (fetchedPages.has(pageNumber)) {
            setIsFetching(false);
            return;
        }
        setIsFetching(true);
        try {
            console.log('[DocumentList] Fetching documents for page:', pageNumber, 'userId:', userId, 'accessToken:', accessToken ? 'Present' : 'Missing');
            const response = await axios.get(
                `${QUIZMASTER_API}/list-documents-by-user/?page=${pageNumber}&limit=${documentsPerPage}`,
                { headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${accessToken}` } }
            );
            console.log('[DocumentList] API response:', {
                status: response.status,
                data: response.data,
                headers: response.headers,
            });
            let data = response.data;
            if (response.status === 401 && supabaseRefreshToken) {
                console.log('[DocumentList] 401 received, attempting token refresh');
                try {
                    const refreshResponse = await axios.post(
                        `${QUIZMASTER_API}/auth/refresh`,
                        { refresh_token: supabaseRefreshToken },
                        { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' } }
                    );
                    const newAccessToken = refreshResponse.data.access_token;
                    const newRefreshToken = refreshResponse.data.refresh_token;
                    console.log('[DocumentList] Token refresh successful, new accessToken:', newAccessToken);
                    onSignIn({ user_id: userId, accessToken: newAccessToken, refreshToken: newRefreshToken });
                    const retryResponse = await axios.get(
                        `${QUIZMASTER_API}/list-documents-by-user/?page=${pageNumber}&limit=${documentsPerPage}`,
                        { headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${newAccessToken}` } }
                    );
                    data = retryResponse.data;
                    console.log('[DocumentList] Retry API response:', {
                        status: retryResponse.status,
                        data: retryResponse.data,
                        headers: retryResponse.headers,
                    });
                } catch (refreshErr) {
                    console.error('[DocumentList] Token refresh failed:', refreshErr);
                    throw new Error('Authentication failed. Please sign in again.');
                }
            }
            if (!data || data.length === 0 || response.status === 204 || response.status === 404) {
                console.log('[DocumentList] No documents found for user');
                setAllDocuments([]);
                setHasMoreDocuments(false);
                setIsFetching(false);
                return;
            }

            const sortedData = data
                .map((doc) => ({
                    ...doc,
                    filename: doc.filename || 'Untitled Document',
                    created_at: doc.created_at || '',
                    updated_at: doc.updated_at || doc.created_at || '',
                }))
                .sort((a, b) => {
                    const bTime = getUtcTimestamp(b.updated_at || b.created_at) ?? 0;
                    const aTime = getUtcTimestamp(a.updated_at || a.created_at) ?? 0;
                    return bTime - aTime;
                });
            setAllDocuments((prev) => {
                const newDocs = [...prev, ...sortedData];
                const uniqueDocs = newDocs.reduce((acc, current) => {
                    const x = acc.find(item => item.id === current.id);
                    if (!x) return acc.concat([current]);
                    return acc;
                }, []);
                console.log('[DocumentList] Updated allDocuments from fetch:', uniqueDocs);
                return uniqueDocs;
            });
            setHasMoreDocuments(sortedData.length === documentsPerPage);
            setFetchedPages((prev) => new Set([...prev, pageNumber]));
            setIsFetching(false);
        } catch (err) {
            console.error('[DocumentList] Error fetching documents:', {
                message: err.message,
                response: err.response ? {
                    status: err.response.status,
                    data: err.response.data,
                    headers: err.response.headers,
                } : 'No response data',
            });
            if (err.response?.status === 404) {
                console.log('[DocumentList] 404 received, treating as no documents found');
                setAllDocuments([]);
                setHasMoreDocuments(false);
                setIsFetching(false);
                return;
            }
            setError(err.message === 'Authentication failed. Please sign in again.'
                ? err.message
                : 'Failed to fetch documents due to a server error. Please try again.');
            setIsFetching(false);
        }
    }, [fetchedPages, accessToken, supabaseRefreshToken, userId, onSignIn]);

    useEffect(() => {
        fetchDocuments(page);
    }, [page, userId, accessToken, supabaseRefreshToken, fetchDocuments]);

    useEffect(() => {
        if (newDocument) {
            console.log('[DocumentList] Received newDocument:', newDocument);
            setAllDocuments((prev) => {
                const exists = prev.some((doc) => doc.id === newDocument.id);
                const existingDoc = exists ? prev.find((doc) => doc.id === newDocument.id) : {};
                const newDoc = {
                    ...existingDoc,
                    ...newDocument,
                    filename: newDocument.filename || existingDoc.filename || 'Untitled Document',
                    updated_at: new Date().toISOString(),
                    created_at: newDocument.created_at || existingDoc.created_at || new Date().toISOString(),
                };
                const updatedDocs = exists
                    ? prev.map((doc) => (doc.id === newDocument.id ? newDoc : doc))
                    : [newDoc, ...prev];
                console.log('[DocumentList] Updated allDocuments with newDoc:', updatedDocs);
                return updatedDocs;
            });
        }
    }, [newDocument]);

    const sortDocuments = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        setAllDocuments(prev => {
            const sorted = [...prev].sort((a, b) => {
                let aValue, bValue;
                switch (key) {
                    case 'filename':
                        aValue = a.filename?.toLowerCase() || '';
                        bValue = b.filename?.toLowerCase() || '';
                        return direction === 'asc'
                            ? aValue.localeCompare(bValue)
                            : bValue.localeCompare(aValue);
                    case 'updated_at':
                    case 'created_at':
                        aValue = getUtcTimestamp(a.updated_at || a.created_at) ?? 0;
                        bValue = getUtcTimestamp(b.updated_at || b.created_at) ?? 0;
                        return direction === 'asc'
                            ? aValue - bValue
                            : bValue - aValue;
                    default:
                        return 0;
                }
            });
            return sorted;
        });
    };

    const formatDate = (dateStr) =>
        formatToLocalDateTime(dateStr, {
            fallback: 'N/A',
            includeSeconds: false,
            dateStyle: 'medium',
            timeStyle: 'short',
            timeZoneName: null,
        });

    const handleDocumentSelect = (doc) => {
        setSelectedDocument(doc);
        setCurrentPage('QuizList', { selectedDocument: doc });
    };

    const handleFlashcardSelect = (doc) => {
        setSelectedDocument(doc);
        setCurrentPage('FlashcardList', { selectedDocument: doc });
    };

    const handlePodcastSelect = (doc) => {
        setSelectedDocument(doc);
        setCurrentPage('PodcastList', { selectedDocument: doc });
    };

    const handleVideoSelect = (doc) => {
        setSelectedDocument(doc);
        setCurrentPage('VideoList', { selectedDocument: doc });
    };

    const filteredDocuments = allDocuments.filter((doc) =>
        (doc.filename || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    const currentTotalPages = Math.ceil(filteredDocuments.length / documentsPerPage);
    const startIndex = (page - 1) * documentsPerPage;
    const endIndex = startIndex + documentsPerPage;
    const documentsToDisplay = filteredDocuments.slice(startIndex, endIndex);
    const showPagination = currentTotalPages > 1 || hasMoreDocuments || page > 1;
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && (newPage <= currentTotalPages || hasMoreDocuments)) {
            setPage(newPage);
        }
    };
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <FaSort size={12} />;
        return sortConfig.direction === 'asc' ? <FaSortUp size={12} /> : <FaSortDown size={12} />;
    };

    if (isFetching && allDocuments.length === 0) {
        return (
            <div className="loading-container">
                <p>Loading documents...</p>
            </div>
        );
    }
    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
                <button className="button confirm-button" onClick={() => { setError(null); fetchDocuments(page); }}>
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="document-list-wrapper">
            <div className="search-container">
                <div className="search-input-wrapper">
                    <FaSearch className="search-icon" aria-hidden="true" />
                    <input
                        type="text"
                        className="search-input search-input--large"
                        placeholder="Search by document name..."
                        aria-label="Search by document name"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(1);
                        }}
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            className="clear-button"
                            onClick={() => {
                                setSearchTerm('');
                                setPage(1);
                            }}
                            title="Clear search"
                            aria-label="Clear search"
                        >
                            <FaTimes className="clear-button-icon" aria-hidden="true" />
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    className="add-document-button"
                    title="Upload new document"
                    aria-label="Upload new document"
                    onClick={() => setCurrentPage('Upload')}
                >
                    <FaPlus aria-hidden="true" />
                </button>
            </div>

            <div className="document-list-container">
                <div className="document-list-controls">
                    <div className="document-sort">
                        <span className="document-sort__label">Sort by</span>
                        <button
                            type="button"
                            className="document-sort-button"
                            onClick={() => sortDocuments('filename')}
                            aria-pressed={sortConfig.key === 'filename'}
                        >
                            Document Name {getSortIcon('filename')}
                        </button>
                        <button
                            type="button"
                            className="document-sort-button"
                            onClick={() => sortDocuments('updated_at')}
                            aria-pressed={sortConfig.key === 'updated_at'}
                        >
                            Upload Date {getSortIcon('updated_at')}
                        </button>
                    </div>
                </div>

                {documentsToDisplay.length > 0 ? (
                    <div className="document-grid">
                        {documentsToDisplay.map((doc) => (
                            <div
                                key={doc.id}
                                className="document-card"
                            >
                                <div className="document-card__header">
                                    <h3 className="document-card__title">{doc.filename}</h3>
                                </div>
                                <div className="document-card__meta">
                                    <span className="document-card__label">Uploaded</span>
                                    <span className="document-card__date">{formatDate(doc.updated_at || doc.created_at)}</span>
                                </div>
                                <div className="document-card__actions">
                                    <span className="document-card__action-link" onClick={() => handleDocumentSelect(doc)}>Open quiz</span>
                                    {doc.token_count > 0 && (
                                        <>
                                            <span className="document-card__action-link" onClick={() => handleFlashcardSelect(doc)}>Open flash card</span>
                                            <span className="document-card__action-link" onClick={() => handlePodcastSelect(doc)}>Open podcast</span>
                                            <span className="document-card__action-link" onClick={() => handleVideoSelect(doc)}>Open video</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="document-empty-state">
                        {searchTerm ? 'No documents match your search.' : 'No documents found.'}
                    </div>
                )}

                {showPagination && (
                    <div className="pagination-container">
                        <button
                            className="button pagination-button"
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1}
                        >
                            Previous
                        </button>
                        <span className="page-info">Page {page} of {currentTotalPages}</span>
                        <button
                            className="button pagination-button"
                            onClick={() => handlePageChange(page + 1)}
                            disabled={!hasMoreDocuments && page >= currentTotalPages}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DocumentList;